import { IComplaintSummary } from "../types/complaintLogistics"

/**
 * Adapter between the existing complaints list API (`GET /complaint/all_complaints`) and the
 * Complaint Reference ID picker on the logistics form.
 *
 * The picker reuses the list endpoint rather than a purpose-built one. It is already paginated and
 * searchable, so it is used as-is: search and every filter go through its own query parameters and
 * whatever it returns for a page is shown, with no client-side re-filtering that would leave pages
 * unevenly sized.
 *
 * Everything that knows the list API's parameter and row field names lives in this one file, so
 * the picker and the summary card never touch those names directly.
 */

/**
 * Selectable statuses. RESOLVED is included because a part is often dispatched just after the
 * complaint is marked resolved.
 */
export const COMPLAINT_PICKER_STATUSES = ["OPEN", "IN_PROGRESS", "DEFERRED", "RESOLVED"]

/** Only complaints raised inside this window can be referenced. */
export const COMPLAINT_PICKER_RAISED_WITHIN_DAYS = 90

const daysAgoAsYYYYMMDD = (days: number) => {
    const date = new Date()
    date.setDate(date.getDate() - days)
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${date.getFullYear()}-${month}-${day}`
}

/**
 * Build the list-API argument for one page of picker results.
 *
 * The endpoint takes a whole filter object (`data`), the page size as `selectedValue`, and the
 * page. Array filters are JSON-stringified by the endpoint itself, so `status` is passed as a
 * plain array here.
 */
export const buildComplaintPickerQueryArg = ({
    page,
    page_size,
    search,
}: {
    page: number
    page_size: number
    search: string
}) => ({
    data: {
        search_key: search,
        status: COMPLAINT_PICKER_STATUSES,
        created_at_start: daysAgoAsYYYYMMDD(COMPLAINT_PICKER_RAISED_WITHIN_DAYS),
        all_complaints: 1,
    },
    selectedValue: page_size,
    page,
})

/**
 * Argument for searching *all* complaints, used by the complaint filter on the logistics list.
 *
 * Deliberately unconstrained, unlike {@link buildComplaintPickerQueryArg}: the raise-time window
 * exists to stop someone attaching a stale complaint to a new dispatch, but filtering a list of
 * tickets that already exist has no such concern — a dispatch raised months ago should still be
 * findable by the complaint it belongs to.
 */
export const buildComplaintSearchQueryArg = ({
    page,
    page_size,
    search,
}: {
    page: number
    page_size: number
    search: string
}) => ({
    data: {
        search_key: search,
        all_complaints: 1,
    },
    selectedValue: page_size,
    page,
})

/**
 * Label for one complaint in a dropdown, and for the locked field once one is chosen.
 *
 * Reads `<complaint id> - <CIN>`, in that order: the numeric id is what a logistics ticket stores
 * and what the list column and detail card show, so it leads. Falls back to the id alone when a
 * complaint carries no CIN, rather than repeating it on both sides of the dash.
 *
 * Defined here so every surface labels a complaint identically: the raise-form picker, the
 * logistics list filter, and the pre-filled field when raising from a complaint drawer.
 */
export const complaintOptionLabel = (complaint: { complaint_id?: any; cin?: any }): string => {
    const id = String(complaint?.complaint_id ?? "").trim()
    const cin = String(complaint?.cin ?? "").trim()
    if (!cin || cin === id) return id
    return `${id} - ${cin}`
}

/** Options for a plain complaint dropdown, built straight from a complaints list response. */
export const selectComplaintOptions = (res: any, page: number) => ({
    options: (res?.data ?? []).map((row: any) => ({
        id: row?.complaint_id,
        title: complaintOptionLabel(row),
        search_key: [row?.station_name, row?.charger_name].filter(Boolean).join(" "),
    })),
    hasMore: hasMoreComplaintPages(res, page),
})

/**
 * Normalise a complaints list row into the summary the picker and the read-only summary card use.
 *
 * The list row names the site `station_name` and the charger `charger_name`; `cin` is the
 * human-facing identifier while `complaint_id` is the key the logistics ticket stores.
 */
export const mapComplaintRowToSummary = (row: any): IComplaintSummary => ({
    complaint_id: Number(row?.complaint_id),
    display_id: String(row?.cin || row?.complaint_id),
    status: row?.status ?? "",
    complaint_type: row?.complaint_type ?? row?.service_complaint_type,
    complaint_sub_type: row?.subtype ?? row?.service_subtype,
    site_name: row?.station_name,
    charger_id: row?.charger_name,
    created_at: row?.created_at,
    owner_name: row?.stati_engineer_name,
    owner_department: row?.department,
    // `user_name`/`user_number` are the contact recorded on the complaint — the closest thing it
    // carries to a site contact, and the destination POC is editable, so it seeds rather than
    // dictates. Left undefined when absent so the POC fields stay blank instead of showing a guess.
    site_poc_name: row?.user_name,
    site_poc_contact: row?.user_number,
})

/** Total pages live under `page.total_pages` on the list response. */
export const hasMoreComplaintPages = (res: any, page: number): boolean => {
    const totalPages = Number(res?.page?.total_pages)
    if (!Number.isFinite(totalPages)) return false
    return page < totalPages
}
